<template>
    <v-container style="max-width: 100%">
        <v-row>
            <v-col>
                <h2 class="py-3">Triage Report</h2>
            </v-col>
            <v-col>
                <v-btn color="primary" class="mx-4 my-4" style="float: right" @click="loadData()">REFRESH</v-btn>
            </v-col>
        </v-row>
        <v-data-table
            data-testId="TriageReportViewerTable"
            :group-by="triageReportGroupBy()"
            :headers="triageReportHeaders()"
            :items="triagedRecordsData"
            item-value="sessionId"
            class="elevation-1 table-striped"
            :sort-by="[{ key: 'lastReviewDate', order: 'desc' }]"
            :loading="recordsLoading"
            loading-text="Loading... Please wait"
            :page="page"
            :items-per-page="itemsPerPage"
            hide-default-footer
        >
            <template v-slot:group-header="{ item, toggleGroup, isGroupOpen }">
                <tr>
                    <td>
                        <VBtn :icon="isGroupOpen(item) ? '$expand' : '$next'" size="small" variant="text" @click="toggleGroup(item)"> </VBtn>
                    </td>
                    <td>{{ item.value }}</td>
                    <td>{{ formatTime(groupAggregateMax(item.items, 'sessionDate')) }}</td>
                    <td>{{ groupAggregateCount(item.items) }}</td>
                    <td>{{ groupAggregateSum(item.items, 'totalRecords') }}</td>
                    <td>{{ groupAggregateSum(item.items, 'totalReviews') }}</td>
                    <td>{{ formatDate(groupAggregateMax(item.items, 'lastReviewDate')) }}</td>
                </tr>
            </template>
            <template v-slot:item="props">
                <tr>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td class="log-col">{{ formatTime(props.item.sessionDate) }}</td>
                    <td class="log-col">
                        <a href="#" @click="loadStudentRecord(props.item)">{{ props.item.studentId }}</a>
                    </td>
                    <td class="log-col">{{ props.item.totalRecords }}</td>
                    <td class="log-col">{{ props.item.totalReviews }}</td>
                    <td class="log-col">{{ formatDate(props.item.lastReviewDate) }}</td>
                </tr>
            </template>

            <template v-slot:bottom>
                <v-toolbar>
                    <v-row style="padding-top: 20px">
                        <v-col cols="9">
                            <v-pagination v-model="page" :length="pageCount"></v-pagination>
                        </v-col>
                        <v-col cols="3">
                            <v-select :items="pagingOptions" label="Page Size" v-model="itemsPerPage" class="mx-2" variant="underlined"> </v-select>
                        </v-col>
                    </v-row>
                </v-toolbar>
            </template>
        </v-data-table>
    </v-container>
</template>

<script lang="ts">
import { DateTime } from 'luxon';
import { RestService } from '../services/rest.service';
import baseComponent from './baseComponent';

export default {
    name: 'TriagedReport',
    extends: baseComponent,
    components: {},
    data: function () {
        return {
            restService: new RestService(),
            recordsLoading: false,
            page: 1,
            itemsPerPage: 100,
            pagingOptions: [50, 100, 500, 1000],
            rawData: [],
            triagedRecordsData: [],
            currentPage: 1,
            totalPages: 1,
            startDate: new Date(),
            endDate: new Date()
        };
    },
    mounted: function () {
        this.loadTriageReport();
    },
    methods: {
        environmentChanged: function () {
            this.loadData();
        },
        loadData() {
            this.page = 1;
            this.currentPage = 1;
            this.itemsPerPage = 100;
            this.startDate = null;
            this.endDate = null;
            this.loadTriageReport();
        },
        loadStudentRecord(item) {
            window.open(location.origin + `/#/${this.environment}/${item.classSessionId}/${item.studentId}`);
        },
        triageReportHeaders() {
            return [
                { title: 'Session id#', key: 'classSessionId' },
                { title: 'Session Date', key: 'sessionDate', dataType: 'Date', width: '180px' },
                { title: 'Student id#', key: 'studentId' },
                { title: 'Total records', key: 'totalRecords' },
                { title: 'Total reviews', key: 'totalReviews' },
                { title: 'Last Review Date', key: 'lastReviewDate', dataType: 'Date', width: '180px' }
            ];
        },
        triageReportGroupBy() {
            return [
                {
                    key: 'classSessionId',
                    order: 'desc'
                }
            ];
        },
        async loadTriageReport() {
            this.recordsLoading = true;
            const response = await this.restService.getTriageReport(this.environment);
            this.rawData = response;
            this.triagedRecordsData = this.rawData;
            this.recordsLoading = false;
            this.filterResults();
        },
        filterResults() {
            // Add filter here
        },
        resetSearch() {
            this.searchText = '';
            this.startDate = null;
            this.endDate = null;
            this.page = 1;
            this.itemsPerPage = 100;
            this.currentPage = 1;
            this.totalPages = 1;
            this.loadTriageReport();
        },
        formatTime(time: number) {
            if (time) {
                const luxonDateObject = DateTime.fromJSDate(new Date(Math.trunc(time * 1000)));
                return luxonDateObject.toLocaleString(DateTime.DATETIME_MED);
            } else {
                return '-';
            }
        },
        formatDate(value: number) {
            if (value) {
                const luxonDateObject = DateTime.fromJSDate(new Date(value));
                return luxonDateObject.toLocaleString(DateTime.DATETIME_MED);
            }
        },
        groupAggregateMax(items: any, column: string) {
            const dates = items.map((o) => o.raw[column]);
            return Math.max(...dates);
        },
        groupAggregateCount(items: any) {
            return items ? items.length : 0;
        },
        groupAggregateSum(items: any, column: string) {
            const values = items.map((o) => o.raw[column]);
            return values.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        }
    },
    computed: {
        pageCount() {
            return Math.ceil(this.triagedRecordsData.length / this.itemsPerPage);
        }
    }
};
</script>

<style lang="scss">
.cursor-pointer {
    cursor: pointer;
}
</style>
